import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useActiveSite } from "../context/ActiveSiteContext";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { apiGet, apiGetBlob, apiPost } from "../api/http";
import { parseApiError } from "../utils/apiErrors";
import { useToast } from "../context/ToastContext";
import { 
  Zap, 
  FileText, 
  AlertTriangle, 
  CheckCircle2, 
  Activity, 
  TrendingUp, 
  Plus, 
  ExternalLink,
  ChevronRight,
  Download,
  Search,
  Code,
  RefreshCw
} from "lucide-react";

// Shared design system imports
import PageHeader from "../components/shared/PageHeader";
import SectionHeader from "../components/shared/SectionHeader";
import Card from "../components/shared/Card";
import Button from "../components/shared/Button";
import MetricCard from "../components/shared/MetricCard";
import EmptyState from "../components/shared/EmptyState";
import DataTable from "../components/shared/DataTable";
import { formatDateOnly } from "../utils/dateTime";

function extractRows(res) {
  const body = res?.data;
  if (Array.isArray(body)) return body;
  if (body && Array.isArray(body.data)) return body.data;
  return [];
}

function dayKey(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function shortUrl(u) {
  if (!u) return "";
  try {
    const s = u.replace(/^https?:\/\//i, "").replace(/\/$/, "");
    return s.length > 28 ? `${s.slice(0, 26)}...` : s;
  } catch {
    return u;
  }
}

function urlMatchesActiveSite(url, activeSite) {
  if (!activeSite || !url) return false;
  try {
    const activeHost = new URL(activeSite.url).hostname.replace(/^www\./i, "").toLowerCase();
    const targetHost = new URL(url).hostname.replace(/^www\./i, "").toLowerCase();
    return activeHost === targetHost || url.toLowerCase().includes(activeHost);
  } catch {
    const activeClean = activeSite.url.replace(/^https?:\/\//i, "").replace(/^www\./i, "").toLowerCase();
    const urlClean = url.replace(/^https?:\/\//i, "").replace(/^www\./i, "").toLowerCase();
    return urlClean.includes(activeClean) || activeClean.includes(urlClean);
  }
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { activeSite, selectSite, sites } = useActiveSite();
  const { showError, showSuccess } = useToast();

  const [activeTab, setActiveTab] = useState("overview"); // overview, trends, competitor
  const [seoForCharts, setSeoForCharts] = useState([]);
  const [deepAudits, setDeepAudits] = useState([]);
  const [loading, setLoading] = useState(true);

  // Competitor state
  const [compareDomainB, setCompareDomainB] = useState("");
  const [competitorLoading, setCompetitorLoading] = useState(false);
  const [competitorData, setCompetitorData] = useState(null);
  const [competitorError, setCompetitorError] = useState("");
  const [forceAudit, setForceAudit] = useState(false);
  const [retryingIds, setRetryingIds] = useState(new Set());

  const triggerRetryPdf = async (auditId) => {
    setRetryingIds((prev) => {
      const next = new Set(prev);
      next.add(auditId);
      return next;
    });
    try {
      await apiPost(`/api/audit/${auditId}/retry-pdf`);
      showSuccess("PDF generation re-triggered!");
      // Reload deep audits
      const deepRes = await apiGet("/api/audit/audits", { page: 1, limit: 30 });
      setDeepAudits(extractRows(deepRes));
    } catch (err) {
      showError(parseApiError(err, "Failed to re-trigger PDF generation"));
    } finally {
      setRetryingIds((prev) => {
        const next = new Set(prev);
        next.delete(auditId);
        return next;
      });
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    setLoading(true);

    const loadData = async () => {
      try {
        const [seoRes, deepRes] = await Promise.all([
          apiGet("/api/seo/audit-history", { page: 1, limit: 30 }),
          apiGet("/api/audit/audits", { page: 1, limit: 30 })
        ]);
        setSeoForCharts(extractRows(seoRes));
        setDeepAudits(extractRows(deepRes));
      } catch (err) {
        showError(parseApiError(err, "Failed to load dashboard data"));
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [isAuthenticated, activeSite?.id, showError]);

  // Filter based on active project
  const filteredDeepAudits = useMemo(() => {
    if (!activeSite) return [];
    return deepAudits.filter(a => urlMatchesActiveSite(a.website_url, activeSite));
  }, [deepAudits, activeSite]);

  const filteredSeoAudits = useMemo(() => {
    if (!activeSite) return [];
    return seoForCharts.filter(a => urlMatchesActiveSite(a.url, activeSite));
  }, [seoForCharts, activeSite]);

  const scoreTrendData = useMemo(() => {
    const sorted = [...filteredDeepAudits].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );
    return sorted.map((row, i) => ({
      key: `${row.id}-${i}`,
      label: formatDateOnly(row.created_at, { month: "short", day: "numeric" }),
      score: Number(row.score) || 0,
      url: shortUrl(row.website_url),
    }));
  }, [filteredDeepAudits]);

  const seoHistoryByDay = useMemo(() => {
    const map = new Map();
    filteredSeoAudits.forEach((row) => {
      const k = dayKey(row.created_at);
      if (!k) return;
      map.set(k, (map.get(k) || 0) + 1);
    });
    return [...map.keys()].sort().map((k) => ({ day: k, count: map.get(k) }));
  }, [filteredSeoAudits]);

  const selectedComparison = useMemo(() => {
    if (filteredDeepAudits.length < 2) return null;
    const sorted = [...filteredDeepAudits].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    return {
      first,
      last,
      runs: filteredDeepAudits.length,
      delta: (Number(last.score) || 0) - (Number(first.score) || 0),
    };
  }, [filteredDeepAudits]);

  const avgScore = useMemo(() => {
    if (!filteredDeepAudits.length) return null;
    const sum = filteredDeepAudits.reduce((acc, r) => acc + (Number(r.score) || 0), 0);
    return Math.round(sum / filteredDeepAudits.length);
  }, [filteredDeepAudits]);

  const recentDelta = useMemo(() => {
    if (filteredDeepAudits.length < 2) return 0;
    const sorted = [...filteredDeepAudits].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const last = Number(sorted[sorted.length - 1]?.score) || 0;
    const prev = Number(sorted[sorted.length - 2]?.score) || 0;
    return last - prev;
  }, [filteredDeepAudits]);

  const alertCount = useMemo(
    () => filteredDeepAudits.filter((audit) => Number(audit.score) < 50).length,
    [filteredDeepAudits]
  );

  const chartTooltipStyle = {
    background: "#fff",
    border: "1px solid var(--border)",
    borderRadius: "8px",
    fontSize: "0.8rem",
  };

  const downloadReport = async (auditId) => {
    try {
      const response = await apiGetBlob(`/api/audit/reports/${auditId}`);
      downloadBlob(response.data, `deep-audit-${auditId}.pdf`);
      showSuccess("Download started.");
    } catch (err) {
      showError(parseApiError(err, "Failed to download report"));
    }
  };

  const runCompetitorCompare = async () => {
    if (!activeSite || !compareDomainB.trim()) return;
    setCompetitorLoading(true);
    setCompetitorError("");
    try {
      const { data } = await apiGet("/api/seo/competitor-compare", {
        primary: activeSite.url,
        competitor: compareDomainB.trim(),
        force: forceAudit,
      });
      setCompetitorData(data);
    } catch (err) {
      setCompetitorError(parseApiError(err, "Failed to compare competitors"));
      setCompetitorData(null);
    } finally {
      setCompetitorLoading(false);
    }
  };

  // Build Actions
  const handleQuickAudit = () => {
    navigate("/seo-audit");
  };

  if (loading) {
    return (
      <div className="page">
        <PageHeader title="Project Overview" subtitle="Loading your SEO dashboard workspace..." />
        <div className="kpi-grid kpi-grid-compact" style={{ marginBottom: "20px" }}>
          <div className="skeleton-block skeleton-card" style={{ height: "120px" }} />
          <div className="skeleton-block skeleton-card" style={{ height: "120px" }} />
          <div className="skeleton-block skeleton-card" style={{ height: "120px" }} />
          <div className="skeleton-block skeleton-card" style={{ height: "120px" }} />
        </div>
        <div className="card skeleton-table">
          {[1, 2, 3, 4].map(i => <div key={i} className="skeleton-block skeleton-row" style={{ height: "50px", marginBottom: "10px" }} />)}
        </div>
      </div>
    );
  }

  // If no projects added
  const allSites = [...(sites.saved || []), ...(sites.recent || [])];
  if (allSites.length === 0 || !activeSite) {
    return (
      <div className="page" style={{ maxWidth: "600px", margin: "60px auto" }}>
        <EmptyState
          title="No Active Project Selected"
          description="Create or select a project from the top navbar selector to load your SEO Dashboard."
          icon={Search}
          actionLabel="Go to SEO Audit to scan a site"
          onAction={() => navigate("/seo-audit")}
        />
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader 
        title="Project Overview" 
        subtitle={`Analyzing project: ${activeSite.url}`}
        actions={
          <div className="dashboard-tabs">
            <button 
              type="button" 
              className={`dashboard-tab-btn ${activeTab === "overview" ? "active" : ""}`}
              onClick={() => setActiveTab("overview")}
            >
              Overview
            </button>
            <button 
              type="button" 
              className={`dashboard-tab-btn ${activeTab === "trends" ? "active" : ""}`}
              onClick={() => setActiveTab("trends")}
            >
              Trends & Analytics
            </button>
            <button 
              type="button" 
              className={`dashboard-tab-btn ${activeTab === "competitor" ? "active" : ""}`}
              onClick={() => setActiveTab("competitor")}
            >
              Competitor Compare
            </button>
          </div>
        }
      />

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <>
          {/* KPI Row */}
          <div className="kpi-grid kpi-grid-compact" style={{ marginBottom: "24px" }}>
            <MetricCard 
              title="Avg. PageSpeed" 
              value={avgScore != null ? `${avgScore}/100` : "--"}
              subtitle={`${filteredDeepAudits.length} runs on record`}
              color="success"
              trend={recentDelta}
              trendLabel=" pts"
              icon={Zap}
            />
            <MetricCard 
              title="SEO Audits" 
              value={filteredSeoAudits.length}
              subtitle="Quick page checkups"
              color="primary"
              icon={FileText}
            />
            <MetricCard 
              title="Deep Audits" 
              value={filteredDeepAudits.length}
              subtitle="Full performance crawls"
              color="warning"
              icon={Activity}
            />
            <MetricCard 
              title="Alerts" 
              value={alertCount}
              subtitle={alertCount > 0 ? "Scores under 50" : "Health status healthy"}
              color={alertCount > 0 ? "danger" : "success"}
              icon={AlertTriangle}
            />
          </div>

          <div className="dashboard-grid-two-col">
            {/* Quick Actions and Limits Column */}
            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              <Card>
                <SectionHeader title="Quick Actions" />
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  <Button variant="primary" onClick={() => navigate("/seo-audit")} style={{ width: "100%" }}>
                    Run SEO Page Audit
                  </Button>
                  <Button variant="secondary" onClick={() => navigate("/pagespeed")} style={{ width: "100%" }}>
                    Test Web PageSpeed
                  </Button>
                  <Button variant="ghost" onClick={() => navigate("/site-crawl")} style={{ width: "100%" }}>
                    Trigger Crawl Job
                  </Button>
                  <Button variant="ghost" onClick={() => navigate("/schedules")} style={{ width: "100%" }}>
                    Configure Monitoring
                  </Button>
                </div>
              </Card>

              {user?.limits && (
                <Card>
                  <SectionHeader title="Usage Limits" />
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px", fontSize: "0.85rem" }}>
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                        <span style={{ fontWeight: 600 }}>Plan Tier</span>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <span className="pill-badge info" style={{ textTransform: "capitalize" }}>{user.plan_name || "Free"}</span>
                          {String(user?.plan_name || "free").toLowerCase() === "free" && (
                            <Link to="/pricing" style={{ fontSize: "0.75rem", fontWeight: 700, color: "#6366f1", textDecoration: "none" }}>Upgrade ✨</Link>
                          )}
                        </div>
                      </div>
                    </div>
                    <div style={{ height: "1px", background: "var(--border)" }} />
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                        <span>Manual Audits (Monthly)</span>
                        <span style={{ fontWeight: 600 }}>{user.usage?.manual_audits_used ?? 0} / {user.limits.manual_audits_per_month ?? 10}</span>
                      </div>
                    </div>
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                        <span>Monitored Sites</span>
                        <span style={{ fontWeight: 600 }}>{user.usage?.monitored_sites ?? 0} / {user.limits.monitored_sites ?? 0}</span>
                      </div>
                    </div>
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                        <span>Active Projects</span>
                        <span style={{ fontWeight: 600 }}>{user.usage?.projects ?? 0} / {user.limits.max_projects ?? 1}</span>
                      </div>
                    </div>
                  </div>
                </Card>
              )}
            </div>

            {/* Recent Activity Table Column */}
            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              <Card>
                <SectionHeader 
                  title="Recent SEO Audits" 
                  actions={
                    <Button variant="ghost" size="sm" onClick={() => navigate("/history?tab=seo")}>
                      View All
                    </Button>
                  } 
                />
                
                <DataTable
                  headers={[
                    { key: "url", label: "Target URL", render: (r) => shortUrl(r.url) },
                    { key: "seo_score", label: "Score", render: (r) => (
                      <span style={{ fontWeight: 600, color: (r.seo_score || 0) >= 60 ? "var(--success)" : "var(--danger)" }}>
                        {r.seo_score ?? "—"}/100
                      </span>
                    )},
                    { key: "created_at", label: "Ran On", render: (r) => formatDateOnly(r.created_at) }
                  ]}
                  data={filteredSeoAudits.slice(0, 5)}
                  keyExtractor={(r) => r.id}
                  emptyState={
                    <EmptyState 
                      title="No Quick Audits Yet" 
                      description="Launch an audit to capture optimization insights for this site."
                    />
                  }
                />
              </Card>

              <Card>
                <SectionHeader 
                  title="Recent Deep Audits" 
                  actions={
                    <Button variant="ghost" size="sm" onClick={() => navigate("/history?tab=deep")}>
                      View All
                    </Button>
                  }
                />
                
                <DataTable
                  headers={[
                    { key: "website_url", label: "URL", render: (r) => shortUrl(r.website_url) },
                    { key: "score", label: "Lighthouse", render: (r) => (
                      <span style={{ fontWeight: 600, color: r.score >= 80 ? "var(--success)" : r.score >= 50 ? "var(--warning)" : "var(--danger)" }}>
                        {r.score}/100
                      </span>
                    )},
                    { key: "report", label: "PDF Export", render: (r) => {
                      const pdfStatus = r.audit_data?.pdfStatus;
                      if (r.pdf_ready) {
                        return (
                          <Button variant="secondary" size="sm" onClick={() => downloadReport(r.id)} icon={Download}>
                            PDF
                          </Button>
                        );
                      }
                      if (pdfStatus === "failed") {
                        return (
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: "0.85rem", color: "var(--danger)", fontWeight: 500 }}>Failed</span>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => triggerRetryPdf(r.id)}
                              disabled={retryingIds.has(r.id)}
                              icon={RefreshCw}
                              style={{ padding: 4 }}
                              title="Retry PDF generation"
                            />
                          </div>
                        );
                      }
                      if (pdfStatus === "pending" || retryingIds.has(r.id)) {
                        return (
                          <span style={{ fontSize: "0.85rem", color: "var(--warning)", display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 500 }}>
                            <RefreshCw size={12} className="animate-spin" />
                            Pending...
                          </span>
                        );
                      }
                      return <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>None</span>;
                    }}
                  ]}
                  data={filteredDeepAudits.slice(0, 5)}
                  keyExtractor={(r) => r.id}
                  emptyState={
                    <EmptyState 
                      title="No Deep Reports" 
                      description="Create a full PDF diagnostic report for this site."
                    />
                  }
                />
              </Card>
            </div>
          </div>
        </>
      )}

      {/* Trends Tab */}
      {activeTab === "trends" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          <div className="dashboard-charts">
            <Card className="dashboard-chart-card">
              <SectionHeader title="Score Trend" subtitle="Lighthouse performance score progression." />
              {scoreTrendData.length === 0 ? (
                <EmptyState title="No Trend History" description="Run multiple deep audits to populate trends." />
              ) : (
                <div className="dashboard-chart-body">
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={scoreTrendData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e5ec" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#64748b" />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} stroke="#64748b" width={36} />
                      <Tooltip contentStyle={chartTooltipStyle} formatter={(v) => [v, "Score"]} />
                      <Line type="monotone" dataKey="score" name="Score" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>

            <Card className="dashboard-chart-card">
              <SectionHeader title="SEO Audit Volume" subtitle="Number of Page Audits generated over time." />
              {seoHistoryByDay.length === 0 ? (
                <EmptyState title="No Audit Records" description="No page-level checks were recorded for this project." />
              ) : (
                <div className="dashboard-chart-body">
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={seoHistoryByDay} margin={{ top: 8, right: 16, left: 0, bottom: 48 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e5ec" />
                      <XAxis
                        dataKey="day"
                        tick={{ fontSize: 10 }}
                        stroke="#64748b"
                        angle={-30}
                        textAnchor="end"
                        height={48}
                      />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#64748b" width={36} />
                      <Tooltip contentStyle={chartTooltipStyle} />
                      <Bar dataKey="count" name="Audits" fill="#2563eb" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </div>

          {selectedComparison && (
            <Card>
              <SectionHeader title="Score Improvement (First vs Latest)" />
              <div className="dashboard-compare-grid">
                <div>
                  <div className="dashboard-compare-label">Target site</div>
                  <div className="dashboard-compare-url">{activeSite.url}</div>
                  <div className="dashboard-compare-runs">{selectedComparison.runs} deep runs</div>
                </div>
                <div className="dashboard-compare-metrics">
                  <div>
                    <div className="dashboard-compare-label">First Score</div>
                    <div className="dashboard-compare-score">{Number(selectedComparison.first.score) || 0}</div>
                    <div className="dashboard-compare-date">
                      {formatDateOnly(selectedComparison.first.created_at)}
                    </div>
                  </div>
                  <div className="dashboard-compare-arrow"><ChevronRight size={20} /></div>
                  <div>
                    <div className="dashboard-compare-label">Latest Score</div>
                    <div className="dashboard-compare-score">{Number(selectedComparison.last.score) || 0}</div>
                    <div className="dashboard-compare-date">
                      {formatDateOnly(selectedComparison.last.created_at)}
                    </div>
                  </div>
                  <div className="dashboard-compare-delta">
                    <div className="dashboard-compare-label">Change</div>
                    <div
                      className={`dashboard-compare-delta-value ${
                        selectedComparison.delta > 0 ? "up" : selectedComparison.delta < 0 ? "down" : ""
                      }`}
                    >
                      {selectedComparison.delta > 0 ? "+" : ""}
                      {selectedComparison.delta}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Competitor Compare Tab */}
      {activeTab === "competitor" && (
        <Card>
          <SectionHeader title="Compare domains" subtitle={`Compare ${activeSite.url} against your SEO competitors.`} />
          
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 16 }}>
            <div style={{ flex: 1, minWidth: "220px" }}>
              <label style={{ fontSize: "0.8rem", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>Primary URL</label>
              <input
                className="input"
                value={activeSite.url}
                disabled
                style={{ width: "100%", background: "#f1f5f9" }}
              />
            </div>
            
            <div style={{ flex: 1, minWidth: "220px" }}>
              <label style={{ fontSize: "0.8rem", color: "var(--text-muted)", display: "block", marginBottom: "4px" }}>Competitor domain</label>
              <input
                className="input"
                placeholder="e.g. competitor.com"
                value={compareDomainB}
                onChange={(e) => setCompareDomainB(e.target.value)}
                style={{ width: "100%" }}
              />
            </div>

            <div style={{ paddingTop: "20px" }}>
              <Button
                variant="primary"
                disabled={competitorLoading || !compareDomainB.trim()}
                onClick={runCompetitorCompare}
                icon={Search}
              >
                {competitorLoading ? "Comparing..." : "Compare"}
              </Button>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
            <input 
              type="checkbox" 
              id="forceAudit" 
              checked={forceAudit} 
              onChange={(e) => setForceAudit(e.target.checked)} 
            />
            <label htmlFor="forceAudit" style={{ fontSize: "0.85rem", color: "var(--text-muted)", cursor: "pointer" }}>
              Force scan competitor domain (slower, bypass cache)
            </label>
          </div>

          {competitorError && <div className="progress-error">{competitorError}</div>}

          {competitorData && (
            <div className="dashboard-charts" style={{ marginTop: 24, gap: "16px" }}>
              <Card style={{ flex: 1, margin: 0, borderLeft: "4px solid var(--accent)" }}>
                <span className="metric-card-title">Primary site</span>
                <h3 style={{ fontSize: "1.1rem", margin: "4px 0" }}>{competitorData.primary.domain}</h3>
                <div style={{ fontSize: "2rem", fontWeight: 700, margin: "8px 0", color: "var(--accent)" }}>
                  {competitorData.primary.score != null ? `${competitorData.primary.score}/100` : "No score"}
                </div>
                <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                  {competitorData.primary.lastAuditedAt ? `Audited: ${formatDateOnly(competitorData.primary.lastAuditedAt)}` : "Not scanned"}
                </span>
              </Card>

              <Card style={{ flex: 1, margin: 0, borderLeft: "4px solid var(--warning)" }}>
                <span className="metric-card-title">Competitor</span>
                <h3 style={{ fontSize: "1.1rem", margin: "4px 0" }}>{competitorData.competitor.domain}</h3>
                <div style={{ fontSize: "2rem", fontWeight: 700, margin: "8px 0", color: "var(--warning)" }}>
                  {competitorData.competitor.score != null ? `${competitorData.competitor.score}/100` : "No score"}
                </div>
                <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                  {competitorData.competitor.lastAuditedAt ? `Audited: ${formatDateOnly(competitorData.competitor.lastAuditedAt)}` : "Not scanned"}
                </span>
              </Card>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default Dashboard;
